/**
 * AJAX Load More — Coleções Shopify
 *
 * Substitui paginação tradicional (full page reload) por
 * carregamento assíncrono de produtos via Shopify Section Rendering API.
 *
 * Funcionamento:
 * 1. Intercepta clique no botão "Carregar Mais"
 * 2. Faz fetch de ?page=N&section_id=XXX
 * 3. Extrai os <li> produtos do HTML retornado
 * 4. Dá append no grid existente
 * 5. Atualiza URL com history.pushState
 * 6. Quando acabar as páginas, esconde o botão
 */

class LoadMore {
  constructor() {
    this.containers = document.querySelectorAll('[data-load-more-container]');
    if (!this.containers.length) return;
    this.init();
  }

  init() {
    // Usamos event delegation no document para que o botão continue
    // funcionando mesmo após o Filterable (Alpine) re-renderizar o
    // #facets-filterable via innerHTML (que destrói listeners diretos).
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-load-more-trigger]');
      if (!trigger) return;

      const container = trigger.closest('[data-load-more-container]');
      if (!container) return;

      e.preventDefault();
      this.loadNextPage(container, trigger);
    });
  }

  async loadNextPage(container, trigger) {
    const nextUrl = trigger.dataset.nextUrl;
    const sectionId = container.dataset.sectionId;
    const currentPage = parseInt(trigger.dataset.currentPage, 10);
    const totalPages = parseInt(trigger.dataset.totalPages, 10);
    const textEl = trigger.querySelector('[data-load-more-text]');
    const spinnerEl = trigger.querySelector('[data-load-more-spinner]');

    if (!nextUrl) return;

    // Mostrar loading state
    trigger.disabled = true;
    trigger.classList.add('pointer-events-none', 'opacity-70');
    if (textEl) textEl.classList.add('hidden');
    if (spinnerEl) spinnerEl.classList.remove('hidden');

    try {
      // Construir URL com section_id para Shopify Section Rendering API
      const url = new URL(nextUrl, window.location.origin);
      url.searchParams.set('section_id', sectionId);

      const response = await fetch(url.toString(), {
        headers: { 'Accept': 'text/html' }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const html = await response.text();

      // Extrair os <li> elements do HTML retornado
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Encontrar o grid de produtos no HTML retornado
      const newGrid = doc.querySelector('#facets-results ul');
      if (!newGrid || !newGrid.children.length) {
        // Sem mais produtos — esconder botão
        this.hideButton(container, trigger);
        return;
      }

      // Encontrar o grid atual na página
      const currentGrid = document.querySelector('#facets-results ul');
      if (!currentGrid) return;

      // Copiar os <li> items para o grid atual
      const fragment = document.createDocumentFragment();
      Array.from(newGrid.children).forEach((li) => {
        // Copiar o elemento para evitar perder event listeners
        const clone = li.cloneNode(true);
        fragment.appendChild(clone);
      });

      // Animação: fade in para novos itens
      Array.from(fragment.children).forEach((child) => {
        child.style.opacity = '0';
        child.style.transform = 'translateY(10px)';
        child.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
      });

      currentGrid.appendChild(fragment);

      // Trigger reflow e animar
      const newItems = currentGrid.querySelectorAll('li[style*="opacity: 0"]');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          newItems.forEach((item) => {
            item.style.opacity = '1';
            item.style.transform = 'translateY(0)';
          });
        });
      });

      // Atualizar URL
      this.updateUrl(nextUrl);

      // Atualizar estado do botão para próxima página
      const nextPage = currentPage + 1;
      trigger.dataset.currentPage = nextPage;

      // Verificar se é a última página
      if (nextPage >= totalPages) {
        this.hideButton(container, trigger);
      } else {
        // Construir URL da próxima página
        const nextPageUrl = this.buildNextPageUrl(window.location.href, nextPage + 1);
        trigger.dataset.nextUrl = nextPageUrl;

        // Atualizar texto
        if (textEl) {
          textEl.textContent = textEl.textContent.replace(
            /\d+/,
            String(nextPage)
          );
        }
      }

      // Scroll suave até os novos itens
      this.scrollToNewItems(newItems);

      // Atualizar contagem de produtos (live region)
      this.updateProductCount();

    } catch (error) {
      console.warn('LoadMore: erro ao carregar produtos — fallback para link tradicional', error);
      // Fallback: navegar para a URL tradicional
      window.location.href = nextUrl;
    } finally {
      // Restaurar botão
      trigger.disabled = false;
      trigger.classList.remove('pointer-events-none', 'opacity-70');
      if (textEl) textEl.classList.remove('hidden');
      if (spinnerEl) spinnerEl.classList.add('hidden');
    }
  }

  buildNextPageUrl(currentUrl, page) {
    const url = new URL(currentUrl, window.location.origin);
    if (page > 1) {
      url.searchParams.set('page', page);
    } else {
      url.searchParams.delete('page');
    }
    return url.toString();
  }

  updateUrl(nextUrl) {
    try {
      const url = new URL(nextUrl, window.location.origin);
      window.history.pushState({ page: url.searchParams.get('page') || 1 }, '', url.toString());
    } catch (e) {
      // Silenciosamente falha — não quebra a experiência
    }
  }

  scrollToNewItems(newItems) {
    if (!newItems || !newItems.length) return;
    const first = newItems[0];
    // Pequeno delay para animação começar
    setTimeout(() => {
      first.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  }

  updateProductCount() {
    const countEl = document.getElementById('facets-results-count');
    if (!countEl) return;
    const grid = document.querySelector('#facets-results ul');
    if (!grid) return;
    const count = grid.children.length;
    // Atualiza texto simples — o locale específico viria do backend,
    // mas para feedback visual já resolve
    countEl.textContent = `${count} produtos`;
  }

  hideButton(container, trigger) {
    const wrapper = trigger.closest('.text-center');
    if (wrapper) {
      wrapper.style.display = 'none';
    }
  }
}

// Inicializar quando o DOM estiver pronto
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new LoadMore());
} else {
  new LoadMore();
}
